import React from 'react';
import { motion } from 'framer-motion';
import { Building2, Users, TrendingUp, MapPin, CheckCircle2, Upload, MousePointerClick, Activity, Award, FileBarChart, PieChart, ArrowRight, Star, Laptop } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
export function CorporatePage() {
  return <div className="min-h-screen bg-cream-50 pt-20 pb-24" data-id="element-340">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-forest-900 via-forest-800 to-forest-950 text-white overflow-hidden py-24 mb-16" data-id="element-341">
        <div className="absolute inset-0 bg-mesh opacity-30" data-id="element-342"></div>
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-ember-500/20 rounded-full blur-3xl" data-id="element-343"></div>
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-forest-400/10 rounded-full blur-3xl" data-id="element-344"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-345">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-346">
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.6
          }} data-id="element-347">
              <Badge variant="gradient" className="mb-6" data-id="element-348">
                <Building2 className="w-3 h-3 mr-1" data-id="element-349" /> Corporate Solutions
              </Badge>
              <h1 className="text-4xl md:text-6xl font-display font-bold leading-tight mb-6" data-id="element-350">
                Train Your{' '}
                <span className="text-ember-400" data-id="element-351">Entire Team Online</span>
              </h1>
              <p className="text-xl text-forest-100 leading-relaxed mb-8" data-id="element-352">
                Manage your team's food safety compliance from one powerful
                online platform. Self-paced courses, real-time tracking, and
                instant certification for businesses of all sizes.
              </p>
              <div className="flex flex-col sm:flex-row gap-4" data-id="element-353">
                <Button size="lg" variant="primary" data-id="element-354">
                  Get a Custom Quote
                </Button>
                <Button size="lg" variant="outline" className="text-white border-white hover:bg-white hover:text-forest-900" data-id="element-355">
                  Explore Platform
                </Button>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="hidden lg:block" data-id="element-356">
              <div className="relative" data-id="element-357">
                <div className="absolute -inset-4 bg-gradient-to-r from-forest-500 to-ember-500 rounded-3xl blur-2xl opacity-30" data-id="element-358"></div>
                <img src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80" alt="Corporate team training" className="relative rounded-3xl shadow-2xl border-4 border-white/10" data-id="element-359" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Key Stats */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-24" data-id="element-360">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6" data-id="element-361">
          {[{
          icon: Building2,
          stat: '500+',
          label: 'Companies'
        }, {
          icon: Users,
          stat: '10,000+',
          label: 'Certified Online'
        }, {
          icon: TrendingUp,
          stat: '98%',
          label: 'Pass Rate'
        }, {
          icon: MapPin,
          stat: '24/7',
          label: 'Access'
        }].map((item, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.1
        }} className="bg-white rounded-3xl p-6 text-center border-2 border-forest-50 shadow-lg" data-id="element-362">
              <div className="w-12 h-12 mx-auto bg-forest-50 rounded-xl flex items-center justify-center mb-4" data-id="element-363">
                <item.icon className="w-6 h-6 text-forest-600" data-id="element-364" />
              </div>
              <p className="text-3xl font-display font-bold text-forest-900 mb-1" data-id="element-365">
                {item.stat}
              </p>
              <p className="text-sm font-bold text-gray-500 uppercase tracking-wider" data-id="element-366">
                {item.label}
              </p>
            </motion.div>)}
        </div>
      </section>

      {/* How it Works */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-24" data-id="element-367">
        <div className="text-center mb-16" data-id="element-368">
          <h2 className="text-3xl md:text-5xl font-display font-bold text-forest-900 mb-4" data-id="element-369">
            How Online Team Training Works
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-370">
            Get your entire team certified in three simple steps — all online.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-371">
          {[{
          title: 'Set Up Your Account',
          desc: 'Create your team manager account and add staff individually or bulk import via CSV. Assign courses with one click.',
          icon: Users
        }, {
          title: 'Staff Complete Training',
          desc: 'Your team completes self-paced video courses on any device, any time. No scheduling conflicts or downtime.',
          icon: Laptop
        }, {
          title: 'Track & Certify',
          desc: 'Monitor real-time progress from your dashboard. Certificates are issued instantly upon completion.',
          icon: Award
        }].map((item, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.1
        }} className="bg-white rounded-3xl p-8 border-2 border-forest-100 shadow-xl hover:border-ember-300 transition-colors group" data-id="element-372">
              <div className="w-16 h-16 bg-gradient-to-br from-forest-50 to-cream-100 rounded-2xl flex items-center justify-center mb-6 border border-forest-100 group-hover:scale-110 transition-transform" data-id="element-373">
                <item.icon className="w-8 h-8 text-forest-600" data-id="element-374" />
              </div>
              <h3 className="text-2xl font-display font-bold text-forest-900 mb-4" data-id="element-375">
                {item.title}
              </h3>
              <p className="text-gray-600 leading-relaxed" data-id="element-376">{item.desc}</p>
            </motion.div>)}
        </div>
      </section>

      {/* Group Management Platform */}
      <section className="bg-white py-24 border-y-2 border-forest-50 mb-24" data-id="element-377">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-378">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center mb-16" data-id="element-379">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} data-id="element-380">
              <Badge variant="warning" className="mb-4" data-id="element-381">
                Platform Features
              </Badge>
              <h2 className="text-3xl md:text-5xl font-display font-bold text-forest-900 mb-6" data-id="element-382">
                Powerful Group Management
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed mb-8" data-id="element-383">
                Take control of your team's compliance with our intuitive
                manager dashboard. Assign courses, track progress, and download
                certificates all in one place.
              </p>
              <Button variant="primary" size="lg" data-id="element-384">
                Request Demo
              </Button>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="relative" data-id="element-385">
              <div className="absolute inset-0 bg-gradient-to-tr from-forest-100 to-ember-100 rounded-3xl transform rotate-3" data-id="element-386"></div>
              <div className="relative bg-white rounded-3xl shadow-2xl border-2 border-forest-50 p-6" data-id="element-387">
                {/* Mock Dashboard UI */}
                <div className="flex items-center justify-between border-b border-gray-100 pb-4 mb-4" data-id="element-388">
                  <div className="font-bold text-forest-900" data-id="element-389">Team Progress</div>
                  <div className="text-sm text-forest-600 bg-forest-50 px-3 py-1 rounded-full" data-id="element-390">
                    85% Compliant
                  </div>
                </div>
                <div className="space-y-4" data-id="element-391">
                  {[1, 2, 3].map(i => <div key={i} className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-xl transition-colors" data-id="element-392">
                      <div className="flex items-center" data-id="element-393">
                        <div className="w-8 h-8 rounded-full bg-forest-100 flex items-center justify-center text-forest-700 font-bold text-xs mr-3" data-id="element-394">
                          U{i}
                        </div>
                        <div data-id="element-395">
                          <div className="text-sm font-bold text-gray-900" data-id="element-396">
                            User {i}
                          </div>
                          <div className="text-xs text-gray-500" data-id="element-397">
                            HACCP Level 2
                          </div>
                        </div>
                      </div>
                      <Badge variant={i === 3 ? 'warning' : 'success'} className="text-[10px]" data-id="element-398">
                        {i === 3 ? 'In Progress' : 'Completed'}
                      </Badge>
                    </div>)}
                </div>
              </div>
            </motion.div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-399">
            {[{
            icon: Upload,
            title: 'Bulk User Upload',
            desc: 'Import your entire team instantly using our simple CSV upload tool.'
          }, {
            icon: MousePointerClick,
            title: 'Course Assignment',
            desc: 'Assign specific courses to individuals or groups with a single click.'
          }, {
            icon: Activity,
            title: 'Progress Tracking',
            desc: 'Monitor real-time completion rates and assessment scores.'
          }, {
            icon: Award,
            title: 'Certificate Management',
            desc: 'Download, verify, and store all staff certificates centrally.'
          }, {
            icon: FileBarChart,
            title: 'Compliance Reporting',
            desc: 'Generate audit-ready reports for EHO inspections instantly.'
          }, {
            icon: PieChart,
            title: 'Team Analytics',
            desc: 'Identify knowledge gaps and track training ROI with detailed insights.'
          }].map((feature, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.1
          }} className="flex items-start p-6 rounded-2xl bg-cream-50 border border-forest-50 hover:shadow-md transition-shadow" data-id="element-400">
                <div className="w-12 h-12 bg-white rounded-xl flex items-center justify-center mr-4 shrink-0 shadow-sm border border-forest-100" data-id="element-401">
                  <feature.icon className="w-6 h-6 text-ember-500" data-id="element-402" />
                </div>
                <div data-id="element-403">
                  <h4 className="font-bold text-forest-900 mb-2" data-id="element-404">
                    {feature.title}
                  </h4>
                  <p className="text-sm text-gray-600 leading-relaxed" data-id="element-405">
                    {feature.desc}
                  </p>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-24" data-id="element-406">
        <div className="text-center mb-16" data-id="element-407">
          <h2 className="text-3xl md:text-5xl font-display font-bold text-forest-900 mb-4" data-id="element-408">
            Plans & Pricing
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-409">
            Scalable solutions designed for businesses of all sizes.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-center" data-id="element-410">
          {/* Team Plan */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="bg-white rounded-3xl p-8 border-2 border-forest-100 shadow-lg" data-id="element-411">
            <h3 className="text-2xl font-display font-bold text-forest-900 mb-2" data-id="element-412">
              Team
            </h3>
            <p className="text-gray-500 mb-6" data-id="element-413">For small businesses</p>
            <div className="text-4xl font-display font-bold text-forest-900 mb-8" data-id="element-414">
              5-25{' '}
              <span className="text-lg text-gray-500 font-sans font-normal" data-id="element-415">
                users
              </span>
            </div>
            <ul className="space-y-4 mb-8" data-id="element-416">
              {['Volume discounts', 'Manager dashboard', 'Basic reporting', 'Email support'].map((f, i) => <li key={i} className="flex items-center text-gray-700" data-id="element-417">
                  <CheckCircle2 className="w-5 h-5 text-forest-500 mr-3 shrink-0" data-id="element-418" />{' '}
                  {f}
                </li>)}
            </ul>
            <Button fullWidth variant="outline" data-id="element-419">
              Get Quote
            </Button>
          </motion.div>

          {/* Business Plan (Highlighted) */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.1
        }} className="bg-gradient-to-br from-forest-900 to-forest-800 rounded-3xl p-8 border-2 border-forest-700 shadow-2xl transform md:-translate-y-4 relative overflow-hidden text-white" data-id="element-420">
            <div className="absolute top-0 right-0 bg-ember-500 text-white px-4 py-1 rounded-bl-xl text-xs font-bold" data-id="element-421">
              MOST POPULAR
            </div>
            <div className="absolute inset-0 bg-mesh opacity-20" data-id="element-422"></div>
            <div className="relative z-10" data-id="element-423">
              <h3 className="text-2xl font-display font-bold mb-2" data-id="element-424">Business</h3>
              <p className="text-forest-200 mb-6" data-id="element-425">For growing organizations</p>
              <div className="text-4xl font-display font-bold mb-8" data-id="element-426">
                25-100{' '}
                <span className="text-lg text-forest-300 font-sans font-normal" data-id="element-427">
                  users
                </span>
              </div>
              <ul className="space-y-4 mb-8" data-id="element-428">
                {['Everything in Team', 'Dedicated account manager', 'Advanced compliance reporting', 'Priority support', 'API access'].map((f, i) => <li key={i} className="flex items-center text-forest-50" data-id="element-429">
                    <CheckCircle2 className="w-5 h-5 text-ember-400 mr-3 shrink-0" data-id="element-430" />{' '}
                    {f}
                  </li>)}
              </ul>
              <Button fullWidth variant="primary" className="shadow-ember-500/20" data-id="element-431">
                Get Quote
              </Button>
            </div>
          </motion.div>

          {/* Enterprise Plan */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: 0.2
        }} className="bg-white rounded-3xl p-8 border-2 border-forest-100 shadow-lg" data-id="element-432">
            <h3 className="text-2xl font-display font-bold text-forest-900 mb-2" data-id="element-433">
              Enterprise
            </h3>
            <p className="text-gray-500 mb-6" data-id="element-434">For large corporations</p>
            <div className="text-4xl font-display font-bold text-forest-900 mb-8" data-id="element-435">
              100+{' '}
              <span className="text-lg text-gray-500 font-sans font-normal" data-id="element-436">
                users
              </span>
            </div>
            <ul className="space-y-4 mb-8" data-id="element-437">
              {['Everything in Business', 'White-label platform', 'SSO integration', 'Bespoke training content', 'On-site training options'].map((f, i) => <li key={i} className="flex items-center text-gray-700" data-id="element-438">
                  <CheckCircle2 className="w-5 h-5 text-forest-500 mr-3 shrink-0" data-id="element-439" />{' '}
                  {f}
                </li>)}
            </ul>
            <Button fullWidth variant="outline" data-id="element-440">
              Contact Sales
            </Button>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <motion.section initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-441">
        <div className="bg-gradient-to-r from-ember-500 to-ember-600 rounded-3xl p-12 text-center text-white shadow-2xl relative overflow-hidden" data-id="element-442">
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl" data-id="element-443"></div>
          <div className="relative z-10" data-id="element-444">
            <h2 className="text-3xl md:text-5xl font-display font-bold mb-6" data-id="element-445">
              Ready to train your team?
            </h2>
            <p className="text-xl text-ember-100 mb-8 max-w-2xl mx-auto" data-id="element-446">
              Join hundreds of Irish businesses that trust us with their food
              safety compliance.
            </p>
            <Link to="/contact" data-id="element-447">
              <Button size="lg" className="bg-white text-ember-600 hover:bg-forest-50 border-white" data-id="element-448">
                Contact Our Corporate Team{' '}
                <ArrowRight className="w-5 h-5 ml-2" data-id="element-449" />
              </Button>
            </Link>
          </div>
        </div>
      </motion.section>
    </div>;
}